import React, { useRef, useState } from 'react';
import { AppleWatchDock } from './AppleWatchDock';
import { auth, db } from '../firebase-config';
import { doc, updateDoc } from 'firebase/firestore';

const AppleWatchModal = ({ isOpen, onClose, onNavigateToPlaying, cachedSelections }) => {
  const selectionsRef = useRef({ moods: [], genres: [] });
  const [currentSelections, setCurrentSelections] = useState({ moods: [], genres: [] });
  const [countdown, setCountdown] = useState(null);
  const isClosingRef = useRef(false);
  const [isFullyVisible, setIsFullyVisible] = useState(false);
  const hasInteractedRef = useRef(false);
  const initialSelectionsRef = useRef(null);

  // Update selections ref when AppleWatchDock notifies of changes
  const handleSelectionsChange = (newSelections) => {
    selectionsRef.current = newSelections;

    // Track initial selections (from Firestore load) vs user changes
    if (initialSelectionsRef.current === null) {
      // This is the first update (from loading saved selections)
      initialSelectionsRef.current = newSelections;
    } else {
      // Check if selections have changed from initial load (user interaction)
      const hasChanged =
        JSON.stringify(newSelections.moods) !== JSON.stringify(initialSelectionsRef.current.moods) ||
        JSON.stringify(newSelections.genres) !== JSON.stringify(initialSelectionsRef.current.genres);

      if (hasChanged && isFullyVisible) {
        hasInteractedRef.current = true;
      }
    }

    setCurrentSelections(newSelections);
  };

  // Set flag when modal opens to indicate panels should be shown
  React.useEffect(() => {
    if (isOpen) {
      // Clear any previous dismissal and signal that panels should show
      sessionStorage.removeItem('vibePanelsDismissed');
      sessionStorage.setItem('appleWatchModalOpened', 'true');
      // Reset flags when modal opens
      isClosingRef.current = false;
      hasInteractedRef.current = false;
      initialSelectionsRef.current = null;

      // Set modal as fully visible after a small delay to ensure CSS rendering is complete
      const visibilityTimer = setTimeout(() => {
        setIsFullyVisible(true);
      }, 300); // 300ms delay to ensure modal and animations are complete

      return () => clearTimeout(visibilityTimer);
    } else {
      // Modal is closing - stop any countdown immediately
      setCountdown(null);
      isClosingRef.current = false;
      setIsFullyVisible(false);
      hasInteractedRef.current = false;
      initialSelectionsRef.current = null;
    }
  }, [isOpen]);

  // Countdown timer when max selections reached (2 moods + 3 genres)
  React.useEffect(() => {
    const { moods, genres } = currentSelections;
    const hasMaxSelections = moods.length === 2 && genres.length === 3;

    if (isFullyVisible && hasMaxSelections && hasInteractedRef.current && countdown === null) {
      // Only start countdown if:
      // 1. Modal is fully visible (after CSS rendering)
      // 2. User has max selections
      // 3. User has actually interacted with the modal (not just loaded previous selections)
      // 4. Countdown not already running
      setCountdown(10);
    } else if (!hasMaxSelections) {
      // Stop countdown if selections changed and no longer at max
      setCountdown(null);
    }
  }, [currentSelections, countdown, isFullyVisible]);

  // Separate effect for countdown decrementing
  React.useEffect(() => {
    if (countdown === null || countdown <= 0) return;

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev === null) return null;
        if (prev <= 1) {
          handleClose();
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [countdown]);

  // Save selections to Firestore when modal closes
  const handleClose = async () => {
    // Prevent multiple executions (race condition with countdown)
    if (isClosingRef.current) return;
    isClosingRef.current = true;

    // Stop countdown immediately when manually closing
    setCountdown(null);

    if (!auth.currentUser) {
      onClose();
      return;
    }

    try {
      // Save to user document
      await updateDoc(doc(db, "users", auth.currentUser.uid), {
        selectedMoods: selectionsRef.current.moods,
        selectedGenres: selectionsRef.current.genres
      });

      // Navigate to playing tab after successful save
      if (onNavigateToPlaying) {
        onNavigateToPlaying();
      }
    } catch (error) {
      console.error("Error saving selections:", error);
    }

    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 overflow-auto bg-black/90">
      <div className="relative w-full max-w-[90vw] max-h-[90vh] overflow-auto bg-[#1a1a2e] rounded-xl border border-white/10 text-center box-border pt-16 pb-0">
        {/* Next Button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-bold text-sm rounded-lg cursor-pointer transition-all duration-300 z-10 shadow-lg"
        >
          NEXT
        </button>

        {/* Header */}
        <div className="px-6 mt-2 mood-vizzy">
          <h2 className="text-2xl font-semibold text-white m-0 mb-1">
            What's your Mood? What genre do you want to hear?
          </h2>
          <p className="text-sm text-white/70 m-0 mb-4">
            Choose up to 2 moods and 3 genres
          </p>
        </div>

        {/* Apple Watch Dock Container - flush to edges */}
        <div className="relative overflow-hidden w-full flex items-center justify-center bg-gradient-to-br from-purple-900/10 via-blue-900/10 to-pink-900/10">
          <AppleWatchDock
            onSelectionsChange={handleSelectionsChange}
            countdown={countdown}
            cachedSelections={cachedSelections}
          />
        </div>
      </div>
    </div>
  );
};

export default AppleWatchModal;
